import { FocusEvent, forwardRef, isValidElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactSelect, { MultiValue, OptionsOrGroups, SelectInstance, SingleValue } from "react-select";

import { LoadingGroup } from "../../general";

import { AccessibilityProps } from "../../AccessibilityProps";
import { Body } from "../../typography";
import { isDescendentOf } from "../../utilities/isDescendentOf";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { useSimpleInputState } from "../useSimpleInputState";
import { normalizeAutocompleteValue } from "../utils/autocompleteHelpers";
import { DropdownProps } from "./Dropdown";
import { GroupItem } from "./GroupItem";
import { MultiSelectionProps } from "./MultiSelectionProps";
import { OptionItem } from "./OptionItem";
import { SharedDropdownPlaceholder } from "./SharedDropdownPlaceholder";
import * as Components from "./components";
import { flattenItems, getItems } from "./helpers";

/**
 * The `SharedDropdown` component properties.
 */
export interface SharedDropdownProps
  extends
    Omit<DropdownProps, "instructions" | "tooltip" | "hideInstructions">,
    MultiSelectionProps,
    Pick<AccessibilityProps, "aria-label"> {
  /**
   * The input ID.
   */
  readonly id: string;
}

/**
 * The shared dropdown components.
 */
export const SharedDropdown = forwardRef<SelectInstance<OptionItem | null, boolean>, SharedDropdownProps>(
  function ShareDropdown(props, ref) {
    const {
      id,
      children,
      prefix,
      suffix,
      placeholder = "Select",
      tabIndex,
      autoFocus,
      defaultValue = null,
      clearable = false,
      searchable = false,
      searchInputProps,
      manualSearchFilter = false,
      noOptionsMessage,
      enableCheckboxOptions,
      showMasterCheck,
      selectedValues,
      label,
      hideLabel,
      "aria-label": ariaLabel,
      onChange,
      onFocusEnter,
      onFocusExit,
      onSearchChange: onSearchChangeProp,
      onMenuScrollToBottom,
      onSelectAll,
      isItemSelected = () => false,
      onRemove,
      autoComplete = "off",
    } = props;
    const { value = defaultValue } = props;

    const getLabelText = (label: React.ReactNode): string | undefined => {
      if (typeof label === "string") return label;
      if (isValidElement(label) && typeof label.props.children === "string") {
        return label.props.children;
      }
      if (Array.isArray(label)) {
        return label.map(getLabelText).filter(Boolean).join(" ");
      }
      return undefined;
    };

    const items: OptionsOrGroups<OptionItem, GroupItem> = getItems(children);
    const flattenedItems = flattenItems(items);
    const [menuState, setMenuState] = useState({
      cancelled: false,
      open: false,
    });
    const parentRef = useRef<HTMLDivElement | null>(null);
    const searchRef = useRef<HTMLInputElement | null>(null);
    const selectRef = useRef<SelectInstance<OptionItem | null, boolean> | null>(null);
    const [searchValue, onSearchChange] = useSimpleInputState<string | null>("");

    const changeHandler = useCallback(
      (newValue: SingleValue<OptionItem | null> | MultiValue<OptionItem | null>) => {
        if (Array.isArray(newValue)) return;
        const singleValue = newValue as SingleValue<OptionItem>;
        onChange?.(singleValue?.value ?? null, value);
      },
      [onChange, value],
    );

    const focusHandler = useCallback(() => {
      onFocusEnter?.();
    }, [searchable, onFocusEnter]);

    const blurHandler = useCallback(
      (ev: FocusEvent) => {
        const { relatedTarget } = ev;

        if (parentRef.current && relatedTarget) {
          if (isDescendentOf(parentRef.current, relatedTarget)) {
            setMenuState((current) => {
              return {
                ...current,
                cancelled: true,
              };
            });
            return;
          }
        }
        // Only call onFocusExit if menu is actually closed
        if (!menuState.open) {
          onFocusExit?.();
        }
      },
      [onFocusExit, menuState.open],
    );

    const menuOpenHandler = useCallback(() => {
      setMenuState((current) => {
        if (current.cancelled) {
          return {
            ...current,
            cancelled: false,
          };
        }

        // Toggle: if menu is already open, close it
        if (current.open) {
          return {
            cancelled: false,
            open: false,
          };
        }

        return {
          cancelled: false,
          open: true,
        };
      });
    }, []);

    const menuCloseHandler = useCallback(() => {
      setMenuState((current) => {
        if (current.cancelled) {
          return {
            ...current,
            cancelled: false,
          };
        }

        return {
          cancelled: false,
          open: false,
        };
      });
    }, []);

    useEffect(() => {
      if (menuState.open) {
        onSearchChange("", "");
        onSearchChangeProp?.("", "");
        /* Focus the search input when the menu opens
           wait a tick to ensure it is rendered */
        const timeoutId = setTimeout(() => {
          searchRef.current?.select();
        }, 100);

        return () => clearTimeout(timeoutId);
      }
      /* onSearchChange and onSearchChangeProp are stable
         expitlicitly ignore them to avoid cleaning searchValue on every change */
    }, [menuState.open]);

    const searchBlurHandler = useCallback(
      (ev: FocusEvent) => {
        const { relatedTarget } = ev;

        if (relatedTarget === null || (parentRef.current && !isDescendentOf(parentRef.current, relatedTarget))) {
          onFocusExit?.();
          setMenuState({
            cancelled: false,
            open: false,
          });
        }
      },
      [onFocusExit],
    );

    const filter = useCallback(
      // eslint-disable-next-line
      (item: any) => {
        if (!searchable) return true;
        if (!searchValue) return true;

        const option = item.data as OptionItem | GroupItem;
        if (option.type === "group") return true;

        if (!option.searchText) {
          console.error("Dropdown with search enabled requires options to either have string content or the label property to be set.");
        } else if (option.searchText.toLowerCase().indexOf(searchValue.toLowerCase()) === -1) return false;

        return true;
      },
      [searchable, searchValue],
    );

    const selectedItem = useMemo(() => {
      if (value === null) return null;
      if (!flattenedItems || items.length === 0) return null;
      return flattenedItems.find((item) => item.value === value) ?? null;
    }, [flattenedItems, value]);

    const searchChangeHandler = useCallback(
      (value: string | null) => {
        onSearchChange(value, searchValue ?? null);
        onSearchChangeProp?.(value, searchValue ?? null);
      },
      [searchValue, onSearchChange, onSearchChangeProp],
    );

    const scrollToBottomHandler = useCallback(() => {
      onMenuScrollToBottom?.();
    }, [onMenuScrollToBottom]);

    const disabled = useInputDisabled(props);
    const readOnly = useInputReadonly(props);
    const submitting = useSubmitting();

    const autocompleteValue = useMemo(() => normalizeAutocompleteValue(autoComplete), [autoComplete]);

    useLayoutEffect(() => {
      if (autoComplete === "on") return;
      const comboboxInput = parentRef.current?.querySelector<HTMLInputElement>("input[role='combobox']");
      if (!comboboxInput) return;

      /* react-select renders a hidden dummy input for non-searchable selects.
         That input does not forward an autocomplete attribute, so Chrome can still
         offer autofill suggestions unless we set it ourselves. */
      comboboxInput.setAttribute("autocomplete", autocompleteValue ?? "off");
      comboboxInput.setAttribute("autocapitalize", "none");
      comboboxInput.setAttribute("autocorrect", "off");
      comboboxInput.setAttribute("spellcheck", "false");
    }, [autocompleteValue, autoComplete, disabled, readOnly, submitting]);

    let finalMenuOpen: boolean | undefined = undefined;
    if (searchable) finalMenuOpen = menuState.open;

    if (readOnly || submitting) finalMenuOpen = false;

    const closeMenuOnSelect = !enableCheckboxOptions;

    // Merge autocomplete into searchInputProps for react-select
    const finalSearchInputProps = useMemo(() => {
      const baseProps = searchInputProps || {};
      return {
        ...baseProps,
        autoComplete: autocompleteValue,
      };
    }, [searchInputProps, autocompleteValue]);
    const accessibleLabel = getLabelText(label) ?? `dropdown ${id}`;

    const refHandler = useCallback(
      (instance: SelectInstance<OptionItem | null, boolean> | null) => {
        selectRef.current = instance;
        if (ref) {
          if (typeof ref === "function") {
            ref(instance);
          } else {
            (ref as React.MutableRefObject<SelectInstance<OptionItem | null, boolean> | null>).current = instance;
          }
        }
      },
      [ref],
    );

    return (
      <LoadingGroup.Placeholder fallback={<SharedDropdownPlaceholder className={props.className} style={props.style} />}>
        <div ref={parentRef}>
          <ReactSelect
            ref={refHandler}
            inputId={id}
            aria-label={ariaLabel ?? accessibleLabel}
            aria-labelledby={!label || hideLabel ? undefined : `${id}-label`}
            autoFocus={autoFocus}
            tabIndex={readOnly || submitting || disabled ? -1 : (tabIndex ?? 0)}
            tabSelectsValue={enableCheckboxOptions && false}
            isSearchable={false}
            isMulti={false}
            isDisabled={disabled}
            isClearable={clearable}
            components={Components}
            placeholder={placeholder}
            options={items}
            value={selectedItem}
            closeMenuOnScroll
            captureMenuScroll
            escapeClearsValue={clearable}
            onChange={changeHandler}
            menuIsOpen={finalMenuOpen}
            openMenuOnClick={!readOnly && !submitting}
            onFocus={focusHandler}
            onBlur={blurHandler}
            onMenuOpen={menuOpenHandler}
            menuShouldScrollIntoView={false}
            closeMenuOnSelect={closeMenuOnSelect}
            onMenuClose={menuCloseHandler}
            onMenuScrollToBottom={scrollToBottomHandler}
            filterOption={!manualSearchFilter ? filter : undefined}
            noOptionsMessage={() => noOptionsMessage || <Body textAlign="center">No options</Body>}
            className={typeof props.className === "string" ? props.className : undefined}
            // custom properties
            {...{
              prefix,
              suffix,
              searchable,
              searchInputProps: finalSearchInputProps,
              searchRef,
              searchValue,
              onSearchChange: searchChangeHandler,
              onSearchBlur: searchBlurHandler,
              isReadOnly: readOnly || submitting,
              enableCheckboxOptions,
              selectedValues,
              showMasterCheck,
              onSelectAll,
              isItemSelected,
              onRemove,
              selectRef,
            }}
          />
        </div>
      </LoadingGroup.Placeholder>
    );
  },
);
